import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { orderProcessorAPI } from '../../services/api';
import { errMsg, EVENT_LABEL, fmtDateTime, inr, isoDaysAgo, RUN_STATUS_LABEL, StatusChip } from './common';

interface Props {
  storeId?: number | null;
  onOpenRun: (runId: number) => void;
}

/** 90-day (default) history of optimizer runs, their decisions and every workflow action. */
const History: React.FC<Props> = ({ storeId, onOpenRun }) => {
  const [view, setView] = useState<'runs' | 'activity'>('runs');
  const [from, setFrom] = useState(isoDaysAgo(90));
  const [to, setTo] = useState(isoDaysAgo(0));
  const [storeOnly, setStoreOnly] = useState(false);
  const [eventType, setEventType] = useState('');
  const [runs, setRuns] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const sid = storeOnly && storeId ? storeId : undefined;
      const [r, e] = await Promise.all([
        orderProcessorAPI.listRuns({ from, to, store_id: sid }),
        orderProcessorAPI.events({ from, to, store_id: sid, event_type: eventType || undefined }),
      ]);
      setRuns(r); setEvents(e); setPage(0);
    } catch (err) { setError(errMsg(err)); }
  }, [from, to, storeOnly, storeId, eventType]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => runs.reduce((a, r) => ({
    runs: a.runs + 1,
    suggested: a.suggested + Number(r.suggested_items || 0),
    accepted: a.accepted + Number(r.accepted_items || 0),
    rejected: a.rejected + Number(r.rejected_items || 0),
    edits: a.edits + Number(r.qty_edits || 0),
    overrides: a.overrides + Number(r.distributor_overrides || 0),
    pos: a.pos + Number(r.po_count || 0),
  }), { runs: 0, suggested: 0, accepted: 0, rejected: 0, edits: 0, overrides: 0, pos: 0 }), [runs]);

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField size="small" type="date" label="From" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="To" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        {storeId && <Button size="small" variant={storeOnly ? 'contained' : 'outlined'} onClick={() => setStoreOnly(!storeOnly)}>{storeOnly ? 'This store' : 'All stores'}</Button>}
        <Button size="small" startIcon={<Refresh />} onClick={load}>Refresh</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {totals.runs} runs · {totals.suggested} suggested · {totals.accepted} accepted · {totals.rejected} rejected ·
          {' '}{totals.edits} qty edits · {totals.overrides} distributor changes · {totals.pos} POs
        </Typography>
      </Stack>

      <Tabs value={view} onChange={(_, v) => { setView(v); setPage(0); }} sx={{ mb: 1 }}>
        <Tab value="runs" label={`Runs (${runs.length})`} />
        <Tab value="activity" label={`Activity (${events.length})`} />
      </Tabs>

      {view === 'runs' && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Run</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Store</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Suggested</TableCell>
                <TableCell align="right">Accepted</TableCell>
                <TableCell align="right">Rejected</TableCell>
                <TableCell align="right">Qty edits</TableCell>
                <TableCell align="right">Dist. changes</TableCell>
                <TableCell align="right">Budget</TableCell>
                <TableCell align="right">Accepted value</TableCell>
                <TableCell align="right">POs</TableCell>
                <TableCell>By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.slice(page * 50, page * 50 + 50).map((r) => (
                <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => onOpenRun(r.id)}>
                  <TableCell><b>{r.run_number}</b></TableCell>
                  <TableCell>{r.run_date}</TableCell>
                  <TableCell>{r.store_name}</TableCell>
                  <TableCell>{r.source}</TableCell>
                  <TableCell><StatusChip status={r.status} /><Typography variant="caption" display="block">{RUN_STATUS_LABEL[r.status]}</Typography></TableCell>
                  <TableCell align="right">{r.suggested_items}</TableCell>
                  <TableCell align="right">{r.accepted_items}</TableCell>
                  <TableCell align="right">{r.rejected_items}</TableCell>
                  <TableCell align="right">{r.qty_edits}</TableCell>
                  <TableCell align="right">{r.distributor_overrides}</TableCell>
                  <TableCell align="right">{inr(r.budget_85)}</TableCell>
                  <TableCell align="right">{inr(r.accepted_value)}</TableCell>
                  <TableCell align="right">{r.po_count}</TableCell>
                  <TableCell><Typography variant="caption">{r.created_by}<br />{fmtDateTime(r.created_at)}</Typography></TableCell>
                </TableRow>
              ))}
              {!runs.length && <TableRow><TableCell colSpan={14}><Typography variant="body2" color="text.secondary">No runs in this range.</Typography></TableCell></TableRow>}
            </TableBody>
          </Table>
          <TablePagination component="div" count={runs.length} page={page} rowsPerPage={50} rowsPerPageOptions={[50]} onPageChange={(_, p) => setPage(p)} />
        </TableContainer>
      )}

      {view === 'activity' && (
        <>
          <FormControl size="small" sx={{ minWidth: 240, mb: 1.5 }}>
            <InputLabel>Action type</InputLabel>
            <Select label="Action type" value={eventType} onChange={(e) => setEventType(e.target.value)}>
              <MenuItem value="">All actions</MenuItem>
              {Object.entries(EVENT_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </Select>
          </FormControl>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Store</TableCell>
                  <TableCell>Run / PO</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Change</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell>By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.slice(page * 50, page * 50 + 50).map((ev) => (
                  <TableRow key={ev.id} hover>
                    <TableCell><Typography variant="caption">{fmtDateTime(ev.created_at)}</Typography></TableCell>
                    <TableCell>{ev.store_name}</TableCell>
                    <TableCell>
                      {ev.run_number && <Button size="small" onClick={() => onOpenRun(ev.run_id)}>{ev.run_number}</Button>}
                      {ev.po_number && <Typography variant="caption" display="block">{ev.po_number}</Typography>}
                    </TableCell>
                    <TableCell>{EVENT_LABEL[ev.event_type] || ev.event_type}</TableCell>
                    <TableCell>{ev.medicine_name || ''}</TableCell>
                    <TableCell>{ev.from_value || ev.to_value ? `${ev.from_value ?? ''} → ${ev.to_value ?? ''}` : ''}</TableCell>
                    <TableCell><Typography variant="caption" sx={{ wordBreak: 'break-word' }}>{ev.details ? Object.entries(ev.details).filter(([, v]) => v !== null).map(([k, v]) => `${k}: ${v}`).join(' · ') : ''}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{ev.created_by}</Typography></TableCell>
                  </TableRow>
                ))}
                {!events.length && <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">No activity in this range.</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
            <TablePagination component="div" count={events.length} page={page} rowsPerPage={50} rowsPerPageOptions={[50]} onPageChange={(_, p) => setPage(p)} />
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default History;
